<template>
    <div class="input-group" :class="size === 'sm' ? 'input-group-sm' : ''">
        <span class="input-group-text">R$</span>
        <input
            ref="inputRef"
            type="text"
            class="form-control text-end font-monospace"
            :value="displayValue"
            :placeholder="placeholder"
            :disabled="disabled"
            @keydown="onKeydown"
            @focus="onFocus"
            @blur="onBlur"
            @paste.prevent="onPaste"
        />
    </div>
</template>

<script>
export default {
    name: 'InputValorBRL',
    props: {
        modelValue: { type: Number, default: 0 },
        casasDecimais: { type: Number, default: 2 },
        size: { type: String, default: 'sm' },
        placeholder: { type: String, default: '0,00' },
        disabled: { type: Boolean, default: false }
    },
    emits: ['update:modelValue', 'enter'],
    data() {
        return {
            // Armazena como inteiro na menor unidade (ex: casasDecimais=2 → centavos)
            rawUnits: 0,
            focused: false
        };
    },
    computed: {
        multiplier() {
            return Math.pow(10, this.casasDecimais);
        },
        displayValue() {
            const val = this.rawUnits / this.multiplier;
            return val.toLocaleString('pt-BR', {
                minimumFractionDigits: this.casasDecimais,
                maximumFractionDigits: this.casasDecimais
            });
        }
    },
    watch: {
        modelValue: {
            immediate: true,
            handler(val) {
                if (!this.focused) {
                    this.rawUnits = Math.round((val || 0) * this.multiplier);
                }
            }
        }
    },
    methods: {
        onKeydown(e) {
            // Dígitos 0-9 (teclado normal e numérico)
            let digit = -1;
            if (e.keyCode >= 48 && e.keyCode <= 57) digit = e.keyCode - 48;
            else if (e.keyCode >= 96 && e.keyCode <= 105) digit = e.keyCode - 96;

            if (digit >= 0) {
                const novo = this.rawUnits * 10 + digit;
                // Limite: 9.999.999,99 (ou equivalente em mais casas)
                if (novo <= 999999999999) {
                    this.rawUnits = novo;
                    this.$emit('update:modelValue', this.rawUnits / this.multiplier);
                }
                e.preventDefault();
                return;
            }

            // Backspace — remove último dígito
            if (e.keyCode === 8) {
                this.rawUnits = Math.floor(this.rawUnits / 10);
                this.$emit('update:modelValue', this.rawUnits / this.multiplier);
                e.preventDefault();
                return;
            }

            // Delete — zera
            if (e.keyCode === 46) {
                this.rawUnits = 0;
                this.$emit('update:modelValue', 0);
                e.preventDefault();
                return;
            }

            // Enter — emite evento para o componente pai
            if (e.keyCode === 13) {
                this.$emit('enter');
                return;
            }

            // Permite: Tab, Escape, setas
            if ([9, 27, 37, 38, 39, 40].includes(e.keyCode)) return;

            // Bloqueia todo o resto
            e.preventDefault();
        },

        onFocus() {
            this.focused = true;
            // Seleciona tudo ao focar
            this.$nextTick(() => {
                this.$refs.inputRef?.select();
            });
        },

        onBlur() {
            this.focused = false;
            // Re-sincroniza caso o modelValue tenha mudado externamente
            this.rawUnits = Math.round((this.modelValue || 0) * this.multiplier);
        },

        onPaste(e) {
            // Aceita colar valores como "1234,56" ou "1.234,56" ou "1234.56"
            const text = (e.clipboardData || window.clipboardData).getData('text');
            // Remove tudo exceto dígitos, vírgula e ponto
            const clean = text.replace(/[^\d,\.]/g, '');
            // Tenta interpretar como número BR (vírgula = decimal) ou EN (ponto = decimal)
            const normalized = clean.includes(',')
                ? clean.replace(/\./g, '').replace(',', '.')
                : clean;
            const num = parseFloat(normalized);
            if (!isNaN(num) && num >= 0) {
                this.rawUnits = Math.round(num * this.multiplier);
                this.$emit('update:modelValue', num);
            }
        },

        // Permite focar o input programaticamente
        focus() {
            this.$refs.inputRef?.focus();
        }
    }
};
</script>
